import React, { useEffect, useRef } from 'react';
import Draggable from 'react-draggable';

import { EmbeddedChat } from '~/components/AiChat/containers/EmbeddedChat/EmbeddedChat';
import { useChatContext } from '~/components/AiChat/hooks/useChat';
import { useDraggableFab } from '~/components/AiChat/hooks/useDraggableFab';
import { CliveAvatar } from '~/components/Clive/CliveAvatar';
import { CLIVE_IMAGES } from '~/components/Clive/cliveRegistry';
import Fab from '~/components/core/Atomic/Fab';
import { useMinimizeBars } from '~/components/core/MinimizedBar/Context/ToastifyBarContext';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';

export const AiChat: React.FC = () => {
  const { isClaimChatEnabled } = useCliveClaimContext();

  // this is for preventing collision between AiChat icon and MinimizedBar
  const { setIsChatEnabled } = useMinimizeBars();

  useEffect(() => {
    setIsChatEnabled(isClaimChatEnabled);
  }, [isClaimChatEnabled, setIsChatEnabled]);

  return isClaimChatEnabled ? <AiChatInner /> : null;
};

export const AiChatInner: React.FC = () => {
  const { isAiChatOpen, setIsAiChatOpen, reloadChat } = useChatContext();
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (isAiChatOpen && !prevIsOpenRef.current) {
      reloadChat().catch(() => {});
    }
    prevIsOpenRef.current = isAiChatOpen;
  }, [isAiChatOpen, reloadChat]);

  return isAiChatOpen ? (
    <EmbeddedChat onClose={() => setIsAiChatOpen(false)} />
  ) : (
    <CliveFab onClick={() => setIsAiChatOpen(true)} />
  );
};

export interface CliveFabProps {
  onClick: () => void;
}

export const CliveFab: React.FC<CliveFabProps> = ({ onClick }) => {
  const { nodeRef, onStart, onDrag, clickHandler } = useDraggableFab(onClick);

  return (
    <div className="z-999 pointer-events-none fixed inset-0">
      <Draggable nodeRef={nodeRef} bounds="parent" onStart={onStart} onDrag={onDrag}>
        <div
          ref={nodeRef}
          className="pointer-events-auto absolute bottom-32 right-32 cursor-grab active:cursor-grabbing"
        >
          <Fab
            className={`h-[76px] w-[76px] border-[5px] border-solid border-blue-100 !bg-transparent ${CLIVE_IMAGES.fab} bg-auto bg-center bg-no-repeat hover:!bg-transparent`}
            onClick={clickHandler}
            aria-label="CliveFab"
          >
            <CliveAvatar mood={CliveAvatar.MOOD.LEFT} size={60} className="z-1000 relative m-0" />
          </Fab>
        </div>
      </Draggable>
    </div>
  );
};
